import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useQuery } from 'react-apollo';
import Helmet from 'react-helmet';

import SignupAlbumLayout from '../../../../../components/global/SignupAlbumLayout';
import FullScreenLoader from '../../../../../components/global/FullScreenLoader';
import AccountAuthOptionStep from './components/AccountAuthOptionStep';
import PinVerifyStep from './components/PinVerifyStep';
import useRosterQrCodeOnboarding from './hooks/useRosterQrCodeOnboarding';
import useOpenAlert from '../../../../../hooks/useOpenAlert';
import withGoogleRecaptchaProvider from '../../../../../components/auth/hocs/withGoogleRecaptchaProvider';
import { RosterQrCodeForOnboarding } from '../../../../../graphql/queries/roster-identitiy';
import { getAsset } from '../../../../../util/assets';
import { getAccountAuthOptions } from './helpers';
import { getErrorString } from '../../../../../util/normalize-errors';

const RosterQrCodeOnboard = () => {
  const { rosterQrCodeId } = useParams();
  const history = useHistory();
  const openAlert = useOpenAlert();
  const authenticated = useSelector(({ auth }) => auth.authenticated);

  const { data: { rosterQrCodeForOnboarding } = {}, loading: queryLoading } =
    useQuery(RosterQrCodeForOnboarding, {
      variables: { roster_qr_code_id: rosterQrCodeId },
      skip: !rosterQrCodeId,
    });

  const accounts = rosterQrCodeForOnboarding?.accounts ?? [];
  const albumFolderId =
    rosterQrCodeForOnboarding?.album_folder?.id ??
    rosterQrCodeForOnboarding?.album?.root_album_folder?.id;

  // The available authentication options. Accounts can expose multiple email
  // addresses (and/or a phone number), so there can be more options than
  // accounts.
  const authOptions = useMemo(
    () => getAccountAuthOptions(accounts),
    [accounts],
  );

  // The selected authentication target ({ account, method, emailAddress }). For
  // scenario A (a single option) this is set automatically, for scenario B
  // (multiple options) it is set once the user picks one of the obfuscated
  // options.
  const [selectedAuth, setSelectedAuth] = useState();

  // Scenario A: a single authentication option is available, so we can
  // automatically pin authenticate them.
  useEffect(() => {
    if (authOptions.length === 1) {
      const [option] = authOptions;
      setSelectedAuth({
        account: option.account,
        method: option.method,
        emailAddress: option.emailAddress,
      });
    }
  }, [authOptions]);

  // Scenario B: multiple authentication options are tied to the QR code, so we
  // present the obfuscated options before requesting a pin.
  const showAuthOptions = authOptions.length > 1 && !selectedAuth;

  const {
    loading,
    error,
    authMethodIsPhone,
    emailAddress,
    phoneNumber,
    requireHumanInput,
    blockLogin,
    onPinVerify,
    onPinResend,
  } = useRosterQrCodeOnboarding({
    account: selectedAuth?.account,
    method: selectedAuth?.method,
    emailAddress: selectedAuth?.emailAddress,
  });

  useEffect(() => {
    if (authenticated && albumFolderId) {
      history.push(`/consumer/matches/${albumFolderId}`);
    }
  }, [authenticated, albumFolderId]);

  const onVerify = async code => {
    try {
      await onPinVerify(code);
    } catch (e) {
      openAlert({
        message: 'There was an error verifying your code',
        info: getErrorString(e),
      });
    }
  };

  if (queryLoading) {
    return <FullScreenLoader />;
  }

  return (
    <Fragment>
      <Helmet>
        <title>Waldo &ndash; Verify</title>
      </Helmet>
      <div className="roster-qr-code-onboard-wrapper">
        <div className="d-flex justify-content-end align-items-center p-3">
          <div className="waldo-logo">
            <img
              src={getAsset('/powered-by-waldo-blue.svg')}
              alt="Waldo"
              style={{ height: '30px' }}
            />
          </div>
        </div>

        <SignupAlbumLayout.Wrapper
          imageDisabled
          logoSrc={null}
          withCTA={false}
          className="roster-qr-code-onboard-layout"
        >
          <div className="roster-qr-code-onboard-content-container">
            {showAuthOptions && (
              <AccountAuthOptionStep
                accounts={accounts}
                onSelect={(account, method, emailAddress) =>
                  setSelectedAuth({ account, method, emailAddress })
                }
              />
            )}
            {selectedAuth?.account && (
              <PinVerifyStep
                authMethodIsPhone={authMethodIsPhone}
                emailAddress={emailAddress}
                phoneNumber={phoneNumber}
                loading={loading}
                error={error}
                requireHumanInput={requireHumanInput}
                blockLogin={blockLogin}
                onVerify={onVerify}
                onPinResend={onPinResend}
                onBack={
                  authOptions.length > 1
                    ? () => setSelectedAuth(undefined)
                    : undefined
                }
              />
            )}
          </div>
        </SignupAlbumLayout.Wrapper>
      </div>
    </Fragment>
  );
};

// The pin request for phone accounts is captcha protected, so the recaptcha
// provider has to be mounted for this (unauthenticated) route.
export default withGoogleRecaptchaProvider(RosterQrCodeOnboard);
